import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useWallets } from '../WalletContext';
import './AddIncomeModal.css';

function AddIncomeModal({ onClose }) {
  const { wallets, selectedWallets, refreshWallets } = useWallets();
  const [formData, setFormData] = useState({
    wallet_id: '',
    date: new Date().toISOString().split('T')[0],
    source: '',
    gross_amount: '',
    income_tax: '',
    social_contributions: '',
    student_loan: '',
    currency: 'EUR',
    notes: ''
  });
  const [showDeductions, setShowDeductions] = useState(false);

  useEffect(() => {
    // Set default wallet to first selected wallet
    if (wallets.length > 0 && selectedWallets.length > 0) {
      const defaultWallet = wallets.find(w => w.id === selectedWallets[0]);
      
      if (defaultWallet) {
        setFormData(prev => ({
          ...prev,
          wallet_id: defaultWallet.id,
          currency: defaultWallet.currency
        }));
      }
    }
  }, [wallets, selectedWallets]);

  const handleWalletChange = (e) => {
    const walletId = parseInt(e.target.value);
    const wallet = wallets.find(w => w.id === walletId);
    setFormData({
      ...formData,
      wallet_id: walletId,
      currency: wallet?.currency || 'EUR'
    });
  };

  const calculateNetAmount = () => {
    const gross = parseFloat(formData.gross_amount) || 0;
    const tax = parseFloat(formData.income_tax) || 0;
    const social = parseFloat(formData.social_contributions) || 0;
    const loan = parseFloat(formData.student_loan) || 0;
    return (gross - tax - social - loan).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const netAmount = calculateNetAmount();
      const response = await api.post('/personal/income', {
        wallet_id: parseInt(formData.wallet_id),
        date: formData.date,
        source: formData.source,
        gross_amount: parseFloat(formData.gross_amount),
        net_amount: parseFloat(netAmount),
        income_tax: parseFloat(formData.income_tax) || 0,
        social_contributions: parseFloat(formData.social_contributions) || 0,
        student_loan: parseFloat(formData.student_loan) || 0,
        currency: formData.currency,
        notes: formData.notes
      });
      
      console.log('Income created:', response.data);
      await refreshWallets();
      onClose();
      window.location.reload(); // Refresh to show new data
    } catch (error) {
      console.error('Error creating income:', error);
      alert('Failed to create income: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'EUR': '€', 'USD': '$', 'GBP': '£', 'CHF': 'CHF',
      'JPY': '¥', 'CAD': 'C$', 'AUD': 'A$', 'CNY': '¥', 'INR': '₹'
    };
    return symbols[currency] || currency;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>💰 Add Income</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Pot *</label>
            <select value={formData.wallet_id} onChange={handleWalletChange} required>
              <option value="">Select pot...</option>
              {wallets.map(wallet => (
                <option key={wallet.id} value={wallet.id}>
                  {wallet.icon || '🫙'} {wallet.name} ({wallet.currency})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Date *</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Source</label>
            <input
              type="text"
              value={formData.source}
              onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              placeholder="e.g., Salary, Freelance, Bonus (optional)"
            />
          </div>

          <div className="form-group">
            <label>Amount ({getCurrencySymbol(formData.currency)}) *</label>
            <input
              type="number"
              step="0.01"
              value={formData.gross_amount}
              onChange={(e) => setFormData({ ...formData, gross_amount: e.target.value })}
              required
            />
          </div>

          <div className="deductions-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={showDeductions}
                onChange={(e) => setShowDeductions(e.target.checked)}
              />
              <span className="toggle-slider"></span>
              <span className="toggle-text">Add deductions (tax, social, loans)</span>
            </label>
          </div>

          {showDeductions && (
            <div className="deductions-section">
              <div className="form-row">
                <div className="form-group">
                  <label>Income Tax ({getCurrencySymbol(formData.currency)})</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.income_tax}
                    onChange={(e) => setFormData({ ...formData, income_tax: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div className="form-group">
                  <label>Social Contributions ({getCurrencySymbol(formData.currency)})</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.social_contributions}
                    onChange={(e) => setFormData({ ...formData, social_contributions: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Student Loan ({getCurrencySymbol(formData.currency)})</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.student_loan}
                  onChange={(e) => setFormData({ ...formData, student_loan: e.target.value })}
                  placeholder="0.00"
                />
              </div>

              <div className="net-amount-display">
                <strong>Net Amount:</strong> {getCurrencySymbol(formData.currency)}{calculateNetAmount()}
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
              placeholder="Optional notes..."
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary">Add Income</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddIncomeModal;
